import { useMemo, useState } from "react"
import { Box, HStack, Input, Stack, VStack, Spinner } from "@chakra-ui/react"
import { Text, Button, toaster } from "@/components"
import { useDealEmails, useMicrosoftConnection } from "@/hooks"
import { useQueryClient } from "@tanstack/react-query"
import { DealEmail } from "@/types"
import { stripPreviewQuote } from "./emailUtils"
import { EmailDetailModal } from "./EmailDetailModal"
import { stageAttachmentAs409A } from "@/api/companies"

interface Props {
  dealId: number
  dealManagerEmail: string
  clientEmail: string
  companyId?: number
  companyFolderPath?: string | null
  dealFolderPath?: string | null
  companyName?: string
}

function formatEmailDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  } catch {
    return d
  }
}

export const DealEmailConversation = ({ dealId, dealManagerEmail, clientEmail, companyId, companyFolderPath, dealFolderPath, companyName }: Props) => {
  const { emails, microsoftConnected, isLoading, refetch } = useDealEmails(dealId)
  const { connect } = useMicrosoftConnection()
  const [selectedEmail, setSelectedEmail] = useState<DealEmail | null>(null)
  const [search, setSearch] = useState("")
  const [stagingInProgress, setStagingInProgress] = useState(false)
  const queryClient = useQueryClient()

  const filteredEmails = useMemo(() => {
    let list = [...emails].sort((a, b) =>
      new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
    )
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) =>
        (e.subject?.toLowerCase().includes(q)) ||
        (e.bodyPreview?.toLowerCase().includes(q)) ||
        (e.fromName?.toLowerCase().includes(q)) ||
        (e.fromAddress.toLowerCase().includes(q))
      )
    }
    return list
  }, [emails, search])

  const handleStageAs409A = companyId ? async (att: { name: string; contentType: string; contentBytes: string }) => {
    setStagingInProgress(true)
    try {
      const result = await stageAttachmentAs409A({
        companyId: companyId!,
        companyName: companyName || null,
        folderPath: companyFolderPath || null,
        contentBytes: att.contentBytes,
        fileName: att.name,
        mimeType: att.contentType,
        clientName: selectedEmail?.fromName || selectedEmail?.fromAddress || null,
      })
      queryClient.invalidateQueries({ queryKey: ["fmvStagingList"] })
      const valueStr = result.fmvValue != null ? ` — $${result.fmvValue}` : ""
      const dateStr = result.fmvDate ? ` (${result.fmvDate})` : ""
      toaster.success({ title: "Staged as 409A", description: `${att.name}${valueStr}${dateStr}` })
      if (result.stagingId) window.open(`/core/fmv_staging/${result.stagingId}`, "_blank")
    } catch {
      toaster.error({ title: "Failed to stage attachment" })
    } finally {
      setStagingInProgress(false)
    }
  } : undefined

  if (isLoading) {
    return (
      <VStack py={10}>
        <Spinner />
        <Text fontSize="sm" color="gray.500">Loading emails...</Text>
      </VStack>
    )
  }

  if (microsoftConnected === false) {
    return (
      <VStack py={10} gap={3}>
        <Text fontSize="md" fontWeight="medium">Connect Microsoft Outlook</Text>
        <Text fontSize="sm" color="gray.500">
          Connect your Microsoft account to view email conversations with this client.
        </Text>
        <Button onClick={() => connect()} size="sm">
          Connect Microsoft Account
        </Button>
      </VStack>
    )
  }

  if (emails.length === 0) {
    return (
      <VStack py={10}>
        <Text fontSize="sm" color="gray.500">No email conversations found with {clientEmail}.</Text>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </VStack>
    )
  }

  return (
    <>
      <Stack gap={3}>
        <HStack justify="space-between">
          <Text fontSize="md" fontWeight="semibold">
            Emails ({emails.length})
          </Text>
          <Button variant="outline" size="xs" onClick={() => refetch()}>
            Refresh
          </Button>
        </HStack>

        <Input
          placeholder="Search emails..."
          size="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {search.trim() && (
          <Text fontSize="xs" color="text.muted">
            {filteredEmails.length} of {emails.length} emails
          </Text>
        )}

        {filteredEmails.length === 0 && search.trim() && (
          <Text fontSize="sm" color="text.muted" textAlign="center" py={4}>
            No emails matching "{search}"
          </Text>
        )}

        {filteredEmails.map((email) => {
          const fromLower = email.fromAddress.toLowerCase()
          const isFromManager = fromLower === dealManagerEmail.toLowerCase() || fromLower.endsWith("@esofund.com")
          return (
            <Box
              key={email.id}
              borderRadius="md" border="1px solid" borderColor="border.default"
              bg={isFromManager ? "rgba(255,255,255,0.02)" : "rgba(59,130,246,0.04)"}
              overflow="hidden" cursor="pointer" _hover={{ bg: "rgba(255,255,255,0.04)" }}
              onClick={() => setSelectedEmail(email)}
              px={3} py={2.5}
            >
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Stack gap={0.5} flex={1} minW={0}>
                  <HStack gap={2}>
                    <Text fontSize="xs" fontWeight="600" color={isFromManager ? "#22c55e" : "#3b82f6"}>
                      {isFromManager ? "You" : "Client"}
                    </Text>
                    <Text fontSize="xs" color="text.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                      {email.fromName || email.fromAddress}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="500" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {email.subject || "(no subject)"}
                  </Text>
                  <Text fontSize="xs" color="text.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {stripPreviewQuote(email.bodyPreview || "")}
                  </Text>
                </Stack>
                <Stack gap={0} alignItems="flex-end" flexShrink={0}>
                  <Text fontSize="2xs" color="text.muted" whiteSpace="nowrap">
                    {formatEmailDate(email.receivedAt)}
                  </Text>
                  {email.hasAttachments && (
                    <Text fontSize="2xs" color="text.muted">Attachment</Text>
                  )}
                </Stack>
              </HStack>
            </Box>
          )
        })}
      </Stack>

      <EmailDetailModal
        email={selectedEmail}
        open={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        dealManagerEmail={dealManagerEmail}
        companyFolderPath={companyFolderPath}
        dealFolderPath={dealFolderPath}
        companyName={companyName}
        onStageAs409A={handleStageAs409A}
        stagingInProgress={stagingInProgress}
      />
    </>
  )
}
